// Output channel layouts the merging service accepts. "auto" exports 5.1 when
// the project's music/effects stems are 5.1, and stereo otherwise.
export const OUTPUT_AUDIO_LAYOUT_OPTIONS = [
    { value: "auto", label: "Auto" },
    { value: "stereo", label: "Stereo" },
    { value: "5.1", label: "5.1" },
];

export const DEFAULT_OUTPUT_AUDIO_LAYOUT = "auto";

// Versions exported before the layout was recorded are stereo.
export const audioLayoutLabel = (entry) => (entry?.audioLayout === "5.1" ? "5.1" : "Stereo");

// Picture an export is muxed onto. Both are offered on every export; the
// lower-resolution copy made at ingestion is the default.
export const VIDEO_SOURCE_OPTIONS = [
    { value: "proxy", label: "Lower resolution (720p)" },
    { value: "original", label: "Original (full resolution)" },
];

export const DEFAULT_VIDEO_SOURCE = "proxy";

// Versions exported before the video source was recorded used the
// lower-resolution copy.
export const videoSourceLabel = (entry) =>
    entry?.videoSource === "original" ? "Original" : "Lower res";

// Video codecs browsers decode (FFprobe names). HEVC only plays where the
// device has a decoder, so it is checked against the browser.
const BROWSER_VIDEO_CODECS = new Set(["h264", "av1", "vp9"]);
const HEVC_MP4_TYPE = 'video/mp4; codecs="hvc1.1.6.L93.B0"';

const canDecode = (codec) => {
    if (BROWSER_VIDEO_CODECS.has(codec)) return true;
    if (codec !== "hevc" || typeof document === "undefined") return false;
    return document.createElement("video").canPlayType(HEVC_MP4_TYPE) !== "";
};

export const PLAYBACK = Object.freeze({
    // Loads with the page, like any lower-resolution export.
    NOW: "now",
    // Loads only once play is pressed.
    ON_PLAY: "on-play",
    // Never loaded into a player; offered as a download.
    DOWNLOAD_ONLY: "download-only",
});

// How a version can play in the browser.
// - Exports onto the original video written with their index first (the
//   merging service records videoCodec on those) play like any other when
//   browsers decode the codec. ProRes, DNxHD and the like never decode.
// - Original exports from before that are fragmented: the browser reads every
//   fragment, thousands of requests for a film, before it can play. The .mp4
//   ones hold a codec browsers decode, so they load only when played; the .mov
//   ones hold ProRes, DNxHD and the like.
export const versionPlayback = (entry) => {
    if (entry?.videoSource !== "original") return PLAYBACK.NOW;
    if (entry.videoCodec) {
        return canDecode(entry.videoCodec) ? PLAYBACK.NOW : PLAYBACK.DOWNLOAD_ONLY;
    }
    return /.mp4$/i.test(entry.url || "") ? PLAYBACK.ON_PLAY : PLAYBACK.DOWNLOAD_ONLY;
};

export const isPlayableVersion = (entry) => versionPlayback(entry) !== PLAYBACK.DOWNLOAD_ONLY;

const createdAt = (entry) => entry?.creationDate?.seconds ?? entry?.creationDate?._seconds ?? -1;

// Newest version, whatever it was exported onto; the list's last entry when
// versions have no creation date. Null for an empty list.
export const latestVersion = (versions) =>
    versions.reduce(
        (newest, entry) => (!newest || createdAt(entry) >= createdAt(newest) ? entry : newest),
        null
    );
